import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import HistoryToggleOffOutlinedIcon from "@mui/icons-material/HistoryToggleOffOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { Box, Typography } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { JumpingDots } from "../../components/UIToolkit/Animations/JumpingDots";
import { ChangeSourceBadge } from "../../components/UIToolkit/Badges/ChangeSourceBadge";
import { MetaChip } from "../../components/UIToolkit/Badges/MetaChip";
import { EmptyState } from "../../components/UIToolkit/molecules/EmptyState";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { useAnnieAsk } from "../../contexts/AnnieAskProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { FONT_SIZE, GRID_RADIUS, INK, NEUTRAL, STATUS } from "../../theme/tokens";
import { FocusedShiftAskAnnieButton } from "./FocusedShiftAskAnnieButton";
import { ShiftSyncTimeline } from "./ShiftSyncTimeline";
import { reasonLabel } from "./shiftLineage";
import { OUTCOME_HINT_KEY, OUTCOME_LABEL_KEY, OUTCOME_TONE, SECTION_LABEL_SX, STEP_LABEL_KEY, STEP_TONE } from "./shiftLineageDisplay";
import { groupEntriesByShift } from "./shiftLineageGroups";
import { useRawSyncFile } from "./useRawSyncFile";
import { useShiftChangeLogQuery } from "./useShiftChangeLogQuery";

/**
 * ONE shift's sync story, opened straight from that shift's raw punches in the day view.
 *
 * The question a support user arrives with is already narrowed ("this punch looks wrong"), so this
 * doesn't make them find the row again: the timeline is open, and the way back out to the employee's
 * whole history is one click. It renders the same `ShiftSyncTimeline` the grid row expands, so the two
 * readings of the same decisions can never diverge.
 */

type Props = {
	shiftId: string;
	nurseId?: number;
	/** Leave focus mode and show this employee's whole log. */
	onClearFocus: () => void;
};

const PANEL_SX = {
	display: "flex",
	flexDirection: "column",
	gap: 2,
	p: 2,
	borderRadius: GRID_RADIUS.PANEL,
	border: `1px solid ${NEUTRAL[200]}`,
	minWidth: 0,
} as const;

/** Last path segment of an S3 key, for a compact one-line label (the full key stays in the tooltip). */
const fileBasename = (key: string): string => key.split("/").pop() || key;

export const FocusedShiftLineage: FC<Props> = ({ shiftId, nurseId, onClearFocus }) => {
	const { t } = useTranslation();
	const { timezone, getNurseFormattedName } = useHospitalData();
	const { track } = useAnalytics();
	const { isSupport, openRawFile, copyFileKey } = useRawSyncFile();
	const { isAvailable: isAnnieAvailable, ask } = useAnnieAsk();

	const { data: entries, isLoading, isError } = useShiftChangeLogQuery(shiftId);

	// The same grouping the table uses. A shift-scoped response is normally ONE group; grouping anyway
	// keeps the two views identical and handles a shift whose decisions recorded different windows.
	const groups = useMemo(() => groupEntriesByShift(entries ?? []), [entries]);

	const employeeName = nurseId === undefined ? null : getNurseFormattedName(nurseId);

	const subject = (() => {
		const startAt = groups[0]?.startAt?.setZone(timezone);
		if (!startAt) return null;
		const endAt = groups[0]?.endAt?.setZone(timezone);
		return {
			date: startAt.toFormat("EEE, MMM d, yyyy"),
			window: endAt ? `${startAt.toFormat("HH:mm")}–${endAt.toFormat("HH:mm")}` : startAt.toFormat("HH:mm"),
		};
	})();

	const handleAskAnnie = () => {
		if (!subject) return;
		// The question names the employee and the day, which is exactly what `explain_shift_sync` resolves
		// — so Annie answers from the same change log this panel is rendering, never from the shift alone.
		const question = t("employeeChangelog.shiftLineage.focus.annieQuestion", {
			employee: employeeName ?? t("employeeChangelog.shiftLineage.focus.thisEmployee"),
			date: subject.date,
			window: subject.window,
		});
		track("shift_change_log_ask_annie", { shift_id: shiftId, nurse_id: nurseId });
		ask(question);
	};

	const header = (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
			<SystemButton
				buttonVariant="borderless"
				size="small"
				leadingIcon={<ArrowBackIcon fontSize="small" />}
				onClick={onClearFocus}
				data-testid="shift-lineage-focus-back"
				noFullWidthMobile
			>
				{t("employeeChangelog.shiftLineage.focus.showAll")}
			</SystemButton>
			<Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
				<Typography sx={{ ...SECTION_LABEL_SX }}>{t("employeeChangelog.shiftLineage.focus.title")}</Typography>
				<Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
					{employeeName && <Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: INK.SECONDARY }}>{employeeName}</Typography>}
					{subject ? (
						<>
							<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY }}>{subject.date}</Typography>
							<Typography sx={{ fontSize: FONT_SIZE.LABEL_XS, color: INK.MUTED }}>{subject.window}</Typography>
						</>
					) : (
						<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.DISABLED }}>{t("employeeChangelog.shiftLineage.shift.noWindow")}</Typography>
					)}
				</Box>
			</Box>
			{/* Needs a shift window to ask ABOUT: `explain_shift_sync` resolves the employee + the day, so a
			    decision with no recorded window has nothing for Annie to look up. */}
			{isAnnieAvailable && subject && <FocusedShiftAskAnnieButton onClick={handleAskAnnie} />}
		</Box>
	);

	const body = (() => {
		if (isLoading) {
			return (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
					<JumpingDots />
				</Box>
			);
		}
		if (isError) {
			return (
				<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, color: STATUS.ERROR.main, textAlign: "center", py: 2 }} data-testid="shift-lineage-focus-error">
					{t("employeeChangelog.shiftLineage.focus.loadError")}
				</Typography>
			);
		}
		if (groups.length === 0) {
			return (
				<EmptyState
					icon={<HistoryToggleOffOutlinedIcon />}
					title={t("employeeChangelog.shiftLineage.focus.empty.title")}
					hint={t("employeeChangelog.shiftLineage.focus.empty.hint")}
					dataTestId="shift-lineage-focus-empty"
				/>
			);
		}
		return (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
				{groups.map((group) => {
					const { source, fileS3Key, syncRunId } = group.latestStep;
					return (
						<Box key={group.key} sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0 }}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
								<MetaChip
									label={t(OUTCOME_LABEL_KEY[group.outcome])}
									tone={OUTCOME_TONE[group.outcome]}
									tooltip={t(OUTCOME_HINT_KEY[group.outcome])}
									dataTestId={`shift-lineage-focus-outcome-${group.key}`}
								/>
								<MetaChip
									label={t(STEP_LABEL_KEY[group.latestStep.status])}
									tone={STEP_TONE[group.latestStep.status]}
									dataTestId={`shift-lineage-focus-step-${group.key}`}
								/>
								<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>{reasonLabel(t, group.latestStep.primaryReason)}</Typography>
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, ml: { sm: "auto" }, minWidth: 0 }}>
									<ChangeSourceBadge source={source} />
									{fileS3Key && (
										// Support can DOWNLOAD the raw file (network-wide → support-gated endpoint);
										// everyone else gets the key as copyable text.
										<MetaChip
											label={fileBasename(fileS3Key)}
											icon={isSupport ? <FileDownloadOutlinedIcon /> : <InsertDriveFileOutlinedIcon />}
											tooltip={isSupport ? t("employeeChangelog.shiftLineage.file.downloadTooltip", { key: fileS3Key }) : fileS3Key}
											onClick={isSupport ? () => void openRawFile(syncRunId) : () => copyFileKey(fileS3Key)}
											ariaLabel={isSupport ? t("employeeChangelog.shiftLineage.file.downloadAria") : t("employeeChangelog.shiftLineage.file.copyAria")}
											dataTestId={`shift-lineage-focus-file-${group.key}`}
											sx={{ minWidth: 0 }}
										/>
									)}
								</Box>
							</Box>
							{/* No maxHeight: this panel is the whole page body, so the timeline grows and the page scrolls. */}
							<ShiftSyncTimeline steps={group.steps} timezone={timezone} subjectKey={group.key} />
						</Box>
					);
				})}
			</Box>
		);
	})();

	return (
		<Box sx={{ height: "100%", overflow: "auto", minWidth: 0 }} data-testid="shift-lineage-focus">
			<Box sx={PANEL_SX}>
				{header}
				{body}
			</Box>
		</Box>
	);
};
